/** The accepted single-field video-intent composer and its honest handoff. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { LoaderCircle } from 'lucide-react';

import { useMediaQuery } from '../hooks/useMediaQuery.js';
import { useLanguage } from '../i18n/language.js';
import { useReducedMotion } from '../hooks/useReducedMotion.js';
import {
  contextSourcesAreComplete,
  contextValueIsValid,
  inspectContextValue,
  normalizeContextValue,
  type ContextPlatform,
  type ContextSource,
} from '../lib/classifyContextUrl.js';
import type { MerchantProfileView } from '../merchant/api.js';
import type { ComposerFlowPhase } from './ContextComposer.js';
import { OnboardingComposer } from './OnboardingComposer.js';
import { INSTAGRAM_INTAKE_COPY } from './instagramIntakeCopy.js';

type AddMode = 'prompt' | 'reference' | null;

export interface ComposerIntent {
  readonly contextNote: string;
  readonly sources: readonly ContextSource[];
}

export interface VideoIntentComposerProps {
  readonly contextNote: string;
  readonly onContextNoteChange: (value: string) => void;
  readonly sources: readonly ContextSource[];
  readonly onAddSource: (source: ContextSource) => void;
  readonly onRemoveSource: (id: string) => void;
  readonly onSubmit: (intent: ComposerIntent) => void;
  readonly phase?: ComposerFlowPhase;
  readonly profileView?: MerchantProfileView | null;
  readonly busy?: boolean;
  readonly externalError?: string | null;
  readonly onConfirmProfile?: () => void;
  readonly onProfileIncorrect?: () => void;
  readonly onRetrySources?: () => void;
  readonly instagramBusinessEnabled?: boolean;
  readonly instagramReanalysisWarning?: boolean;
}

function platformLabel(
  platform: ContextPlatform,
  copy: ReturnType<typeof useLanguage>['copy'],
): string {
  if (platform === 'instagram') return copy.composer.instagram;
  if (platform === 'tiktok') return copy.composer.tiktok;
  return copy.composer.website;
}

export function VideoIntentComposer({
  contextNote,
  onContextNoteChange,
  sources,
  onAddSource,
  onRemoveSource,
  onSubmit,
  phase = 'input',
  profileView = null,
  busy = false,
  externalError = null,
  onConfirmProfile = () => undefined,
  onProfileIncorrect = () => undefined,
  onRetrySources = () => undefined,
  instagramBusinessEnabled = false,
  instagramReanalysisWarning = false,
}: VideoIntentComposerProps): React.ReactElement {
  const { copy, language } = useLanguage();
  const instagramCopy = INSTAGRAM_INTAKE_COPY[language];
  const reducedMotion = useReducedMotion();
  // The empty composer shows one example intent at a time and moves to the
  // next every few seconds; it stops as soon as the merchant is typing.
  const [exampleIndex, setExampleIndex] = useState(0);
  const narrow = useMediaQuery('(max-width: 600px)');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const examples = copy.composer.examplePrompts;
  useEffect(() => {
    if (reducedMotion || focused || draft.length > 0 || examples.length < 2)
      return undefined;
    const timer = window.setInterval(() => {
      setExampleIndex((index) => (index + 1) % examples.length);
    }, 3600);
    return () => {
      window.clearInterval(timer);
    };
  }, [reducedMotion, focused, draft.length, examples.length]);
  const [promptBackup, setPromptBackup] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [liveMessage, setLiveMessage] = useState('');

  useEffect(() => {
    if (phase !== 'input') {
      setDraft('');
      setEditingPrompt(false);
      setAddMode(null);
    }
  }, [phase]);

  useEffect(() => {
    // Status and recovery copy belongs to the language in which it was
    // produced. Clear it on a language switch instead of leaving an English
    // announcement visible inside an Arabic composer (or vice versa).
    setLiveMessage('');
    setLocalError(null);
  }, [copy]);

  const conflictFor = useCallback(
    (source: ContextSource): string | null => {
      const normalized = normalizeContextValue(source.url);
      if (
        sources.some((current) => normalizeContextValue(current.url) === normalized)
      ) {
        return copy.composer.errorAlreadyAttached;
      }
      if (sources.some((current) => current.platform === source.platform)) {
        if (source.platform === 'website') {
          return copy.composer.errorWebsiteOccupied;
        }
        if (source.platform === 'tiktok') {
          return copy.composer.errorTikTokOccupied;
        }
        return copy.composer.errorInstagramOccupied;
      }
      if (
        source.platform !== 'website' &&
        sources.some((current) => current.platform !== 'website')
      ) {
        return source.platform === 'tiktok'
          ? copy.composer.errorTikTokOccupied
          : copy.composer.errorInstagramOccupied;
      }
      if (
        instagramBusinessEnabled &&
        ((source.platform === 'website' &&
          sources.some(
            (current) =>
              current.platform === 'instagram' &&
              !contextValueIsValid(current.url, 'instagram'),
          )) ||
          (source.platform === 'instagram' &&
            !contextValueIsValid(source.url, 'instagram') &&
            sources.some((current) => current.platform === 'website')))
      )
        return instagramCopy.singleSource;
      return null;
    },
    [copy.composer, sources, instagramBusinessEnabled, instagramCopy.singleSource],
  );

  const clearDraftState = useCallback(() => {
    setDraft('');
    setEditingPrompt(false);
    setPromptBackup('');
    setAddMode(null);
    setLocalError(null);
  }, []);

  const attachSource = useCallback(
    (source: ContextSource): readonly ContextSource[] | null => {
      const conflict = conflictFor(source);
      if (conflict !== null) {
        setLocalError(conflict);
        return null;
      }
      const next = [...sources, source];
      onAddSource(source);
      clearDraftState();
      setLiveMessage(
        source.platform === 'website'
          ? copy.composer.productAdded
          : copy.composer.referenceAdded,
      );
      return next;
    },
    [clearDraftState, conflictFor, copy.composer, onAddSource, sources],
  );

  const resolveDraft = useCallback((): ComposerIntent | null => {
    const value = draft.trim();
    if (value.length === 0) return { contextNote, sources };

    const inspected = inspectContextValue(value, { instagramBusinessEnabled });
    if (inspected.kind === 'unsupported') {
      setLocalError(copy.composer.errorUnsupported);
      return null;
    }
    if (inspected.kind === 'invalid') {
      setLocalError(
        instagramBusinessEnabled
          ? instagramCopy.invalidLink
          : copy.composer.errorNotALink,
      );
      return null;
    }
    if (inspected.kind === 'source') {
      const nextSources = attachSource(inspected.source);
      if (nextSources === null) return null;
      return {
        contextNote: editingPrompt ? promptBackup : contextNote,
        sources: nextSources,
      };
    }

    if (addMode === 'reference') {
      setLocalError(copy.composer.errorNotALink);
      return null;
    }
    if (contextNote.trim().length > 0 && !editingPrompt) {
      setLocalError(copy.composer.promptExists);
      return null;
    }

    onContextNoteChange(value);
    clearDraftState();
    setLiveMessage(copy.composer.promptAdded);
    return { contextNote: value, sources };
  }, [
    addMode,
    attachSource,
    clearDraftState,
    contextNote,
    copy.composer,
    draft,
    editingPrompt,
    onContextNoteChange,
    promptBackup,
    sources,
    instagramBusinessEnabled,
    instagramCopy.invalidLink,
  ]);

  const commitDraft = useCallback(() => {
    if (busy || draft.trim().length === 0) return;
    resolveDraft();
  }, [busy, draft, resolveDraft]);

  const submitIntent = useCallback(() => {
    if (busy) return;
    const finalIntent = resolveDraft();
    if (finalIntent === null) return;
    if (!contextSourcesAreComplete(finalIntent.sources, { instagramBusinessEnabled })) {
      setLocalError(
        instagramBusinessEnabled
          ? instagramCopy.needsSource
          : copy.composer.promptNeedsProduct,
      );
      return;
    }
    onSubmit(finalIntent);
  }, [
    busy,
    copy.composer.promptNeedsProduct,
    onSubmit,
    resolveDraft,
    instagramBusinessEnabled,
    instagramCopy.needsSource,
  ]);

  const selectMode = useCallback(
    (mode: Exclude<AddMode, null>) => {
      if (mode === 'prompt' && contextNote.trim().length > 0 && !editingPrompt) {
        setLocalError(copy.composer.promptExists);
        inputRef.current?.focus();
        return;
      }
      setAddMode(mode);
      setLocalError(null);
      setLiveMessage(
        mode === 'prompt' ? copy.composer.promptHint : copy.composer.referenceHint,
      );
      inputRef.current?.focus();
    },
    [contextNote, copy.composer, editingPrompt],
  );

  if (phase !== 'input') {
    return (
      <OnboardingComposer
        contextNote={contextNote}
        sources={sources}
        phase={phase}
        profileView={profileView}
        busy={busy}
        externalError={externalError}
        onConfirmProfile={onConfirmProfile}
        onProfileIncorrect={onProfileIncorrect}
        onRetrySources={onRetrySources}
      />
    );
  }

  // A website typed but not yet attached already counts: the merchant does
  // not have to press Enter before "Create videos" lights up. Submitting
  // attaches it through the same `resolveDraft` path.
  const draftSource = (() => {
    const value = draft.trim();
    if (value.length === 0) return null;
    const inspected = inspectContextValue(value, { instagramBusinessEnabled });
    return inspected.kind === 'source' && conflictFor(inspected.source) === null
      ? inspected.source
      : null;
  })();
  const submitDisabled =
    busy ||
    !contextSourcesAreComplete(
      draftSource === null ? sources : [...sources, draftSource],
      { instagramBusinessEnabled },
    );
  const placeholder =
    addMode === 'prompt'
      ? copy.composer.promptPlaceholder
      : addMode === 'reference'
        ? copy.composer.referencePlaceholder
        : (examples[exampleIndex % examples.length] ?? copy.composer.urlPlaceholder);

  return (
    <section
      className="mj-video-composer"
      data-layout={narrow ? 'mobile' : 'desktop'}
      data-level={focused ? 'focus' : 'rest'}
      aria-label={copy.composer.composerRegion}
    >
      <div
        className="mj-video-composer-surface"
        data-testid="context-composer-surface"
        aria-busy={busy}
      >
        <div className="mj-video-composer-input-wrap">
          <label htmlFor="video-intent" className="mj-visually-hidden">
            {copy.composer.urlLabel}
          </label>
          <div className="mj-video-composer-prompt-row">
            <span className="mj-video-composer-prompt-icon" aria-hidden="true">
              ✦
            </span>
            <textarea
              ref={inputRef}
              id="video-intent"
              rows={1}
              value={draft}
              disabled={busy}
              autoComplete="off"
              spellCheck="true"
              dir={draft.length > 0 ? 'auto' : language === 'ar' ? 'rtl' : 'ltr'}
              placeholder={placeholder}
              aria-invalid={localError !== null}
              aria-describedby="video-composer-helper video-composer-status"
              onChange={(event) => {
                setDraft(event.target.value);
                setLocalError(null);
              }}
              onFocus={() => {
                setFocused(true);
              }}
              onBlur={() => {
                setFocused(false);
              }}
              onPaste={(event) => {
                const pasted = event.clipboardData.getData('text').trim();
                const inspected = inspectContextValue(pasted, {
                  instagramBusinessEnabled,
                });
                if (inspected.kind === 'text') return;
                event.preventDefault();
                if (inspected.kind === 'unsupported') {
                  setDraft(pasted);
                  setLocalError(copy.composer.errorUnsupported);
                  return;
                }
                if (inspected.kind === 'invalid') {
                  setDraft(pasted);
                  setLocalError(
                    instagramBusinessEnabled && pasted.includes('instagram')
                      ? instagramCopy.invalidLink
                      : copy.composer.errorNotALink,
                  );
                  return;
                }
                const conflict = conflictFor(inspected.source);
                if (conflict !== null) {
                  setDraft(pasted);
                  setLocalError(conflict);
                  return;
                }
                attachSource(inspected.source);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Escape' && editingPrompt) {
                  event.preventDefault();
                  clearDraftState();
                  setLiveMessage(copy.composer.promptAdded);
                  return;
                }
                if (event.key !== 'Enter' || event.shiftKey) return;
                event.preventDefault();
                commitDraft();
              }}
            />
          </div>

          {(contextNote.trim().length > 0 && !editingPrompt) || sources.length > 0 ? (
            <ul className="mj-video-source-list" aria-label={copy.composer.addedInputs}>
              {contextNote.trim().length > 0 && !editingPrompt ? (
                <li className="mj-video-source-chip" data-kind="prompt">
                  <span className="mj-video-source-signal" aria-hidden="true" />
                  <span className="mj-video-source-copy">
                    <span className="mj-video-source-kind">
                      {copy.composer.promptChip}
                    </span>
                    <button
                      type="button"
                      className="mj-video-prompt-edit"
                      aria-label={copy.composer.editPrompt}
                      onClick={() => {
                        setPromptBackup(contextNote);
                        setEditingPrompt(true);
                        setDraft(contextNote);
                        setAddMode('prompt');
                        setLocalError(null);
                        window.requestAnimationFrame(() => inputRef.current?.focus());
                      }}
                    >
                      {contextNote}
                    </button>
                  </span>
                  <button
                    type="button"
                    className="mj-video-source-remove"
                    aria-label={copy.composer.removePrompt}
                    onClick={() => {
                      onContextNoteChange('');
                      setLiveMessage(copy.composer.promptRemoved);
                      setLocalError(null);
                      window.requestAnimationFrame(() => inputRef.current?.focus());
                    }}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </li>
              ) : null}
              {sources.map((source) => {
                const kind =
                  source.platform === 'website'
                    ? copy.composer.productSource
                    : instagramBusinessEnabled &&
                        sources.length === 1 &&
                        source.platform === 'instagram'
                      ? instagramCopy.source
                      : copy.composer.referenceSource;
                return (
                  <li
                    key={source.id}
                    className="mj-video-source-chip"
                    data-kind={source.platform === 'website' ? 'product' : 'reference'}
                    aria-label={`${kind}: ${source.label}`}
                  >
                    <span className="mj-video-source-signal" aria-hidden="true" />
                    <span className="mj-video-source-copy">
                      <span className="mj-video-source-kind">
                        {kind} · {platformLabel(source.platform, copy)}
                      </span>
                      <span className="mj-video-source-label" dir="ltr">
                        {source.label}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="mj-video-source-remove"
                      aria-label={copy.composer.removeContext(source.label)}
                      onClick={() => {
                        onRemoveSource(source.id);
                        setLiveMessage(copy.composer.sourceRemovedPlain);
                        setLocalError(null);
                        window.requestAnimationFrame(() => inputRef.current?.focus());
                      }}
                    >
                      <span aria-hidden="true">×</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>

        <div
          className="mj-video-composer-foot"
          data-tone={localError ? 'error' : 'status'}
        >
          <p
            id="video-composer-helper"
            className={
              localError ? 'mj-video-composer-error' : 'mj-video-composer-helper'
            }
            role={localError ? 'alert' : undefined}
          >
            {localError ??
              (addMode === 'prompt'
                ? copy.composer.promptHint
                : addMode === 'reference'
                  ? copy.composer.referenceHint
                  : instagramBusinessEnabled
                    ? sources.length === 1 && sources[0]?.platform === 'tiktok'
                      ? instagramCopy.needsSource
                      : instagramCopy.hint
                    : copy.composer.linkHint)}
            {instagramBusinessEnabled &&
            instagramReanalysisWarning &&
            ((sources.length === 1 && sources[0]?.platform === 'instagram') ||
              (sources.length === 0 && draftSource?.platform === 'instagram')) ? (
              <span> {instagramCopy.reanalysis}</span>
            ) : null}
          </p>
        </div>

        <div className="mj-video-composer-footer">
          <div
            className="mj-video-composer-actions"
            role="group"
            aria-label={copy.composer.videoInputs}
          >
            <button
              type="button"
              className="mj-video-composer-secondary"
              data-source-mode="prompt"
              aria-pressed={addMode === 'prompt'}
              onClick={() => {
                selectMode('prompt');
              }}
            >
              <span
                className="mj-video-composer-cubes"
                data-tone="prompt"
                aria-hidden="true"
              />
              {copy.composer.addPrompt}
            </button>
            <button
              type="button"
              className="mj-video-composer-secondary"
              data-source-mode="reference"
              aria-pressed={addMode === 'reference'}
              onClick={() => {
                selectMode('reference');
              }}
            >
              <span
                className="mj-video-composer-cubes"
                data-tone="reference"
                aria-hidden="true"
              />
              {copy.composer.addReference}
            </button>
            <button
              type="button"
              className="mj-video-composer-ratio"
              aria-label={copy.composer.aspectRatio}
              aria-pressed="true"
            >
              <span
                className="mj-video-composer-cubes"
                data-tone="ratio"
                aria-hidden="true"
              />
              9:16
            </button>
          </div>

          <button
            type="button"
            className="mj-video-composer-submit"
            onClick={submitIntent}
            disabled={submitDisabled}
          >
            <span>{busy ? copy.composer.submitting : copy.composer.submit}</span>
            {busy ? (
              <LoaderCircle
                size={16}
                strokeWidth={1.6}
                className="mj-spinner"
                aria-hidden="true"
              />
            ) : null}
          </button>
        </div>

        <span
          id="video-composer-status"
          className="mj-visually-hidden"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {liveMessage}
        </span>
      </div>

      {externalError !== null ? (
        <p className="mj-video-composer-external-error" role="alert">
          {externalError}
        </p>
      ) : null}
    </section>
  );
}
